import { ComboConfig, ComboItem, Product } from "../models";
import {
  areProductOptionsEqual,
  sortProductOptions,
  getTotalPriceForProductOptions,
} from "./Modifier";

export const areComboProductsEqual = (
  currentComboProducts?: Product[],
  comboProductsToCheck?: Product[]
) => {
  if (
    !currentComboProducts ||
    !comboProductsToCheck ||
    currentComboProducts.length !== comboProductsToCheck.length
  ) {
    return false;
  }

  return (
    currentComboProducts.every((currentProduct: Product) => {
      return comboProductsToCheck.some((productToCheck: Product) => {
        return (
          currentProduct.productId === productToCheck.productId &&
          areProductOptionsEqual(
            currentProduct.productOptions ?? [],
            productToCheck.productOptions ?? []
          )
        );
      });
    }) &&
    comboProductsToCheck.every((productToCheck: Product) => {
      return currentComboProducts.some((currentProduct: Product) => {
        return (
          productToCheck.productId === currentProduct.productId &&
          areProductOptionsEqual(
            productToCheck.productOptions ?? [],
            currentProduct.productOptions ?? []
          )
        );
      });
    })
  );
};

export const areComboConfigEqual = (
  currentComboConfig: ComboConfig[],
  comboConfigToCheck: ComboConfig[],
  isMergeSimilar = false
): boolean => {
  if (
    !currentComboConfig ||
    !comboConfigToCheck ||
    currentComboConfig.length !== comboConfigToCheck.length
  ) {
    return false;
  }

  if (isMergeSimilar) {
    const mergedCurrentComboConfig =
      mergeSimilarComboItemConfig(currentComboConfig);
    const mergedComboConfigToCheck =
      mergeSimilarComboItemConfig(comboConfigToCheck);

    return (
      mergedCurrentComboConfig.every((currentComboConfigItem: ComboConfig) => {
        return mergedComboConfigToCheck.some(
          (comboConfigItemToCheck: ComboConfig) => {
            return (
              currentComboConfigItem.comboConfigId ===
                comboConfigItemToCheck.comboConfigId &&
              comboConfigItemToCheck.quantity ===
                currentComboConfigItem.quantity &&
              areComboProductsEqual(
                currentComboConfigItem.comboItemProducts,
                comboConfigItemToCheck.comboItemProducts
              )
            );
          }
        );
      }) &&
      mergedComboConfigToCheck.every((comboConfigItemToCheck: ComboConfig) => {
        return mergedCurrentComboConfig.some(
          (currentComboConfigItem: ComboConfig) => {
            return (
              currentComboConfigItem.comboConfigId ===
                comboConfigItemToCheck.comboConfigId &&
              comboConfigItemToCheck.quantity ===
                currentComboConfigItem.quantity &&
              areComboProductsEqual(
                comboConfigItemToCheck.comboItemProducts,
                currentComboConfigItem.comboItemProducts
              )
            );
          }
        );
      })
    );
  }

  return (
    currentComboConfig.every((currentComboConfigItem: ComboConfig) => {
      return comboConfigToCheck.some((comboConfigItemToCheck: ComboConfig) => {
        return (
          currentComboConfigItem.comboConfigId ===
            comboConfigItemToCheck.comboConfigId &&
          areComboProductsEqual(
            currentComboConfigItem.comboItemProducts,
            comboConfigItemToCheck.comboItemProducts
          )
        );
      });
    }) &&
    comboConfigToCheck.every((comboConfigItemToCheck: ComboConfig) => {
      return currentComboConfig.some((currentComboConfigItem: ComboConfig) => {
        return (
          currentComboConfigItem.comboConfigId ===
            comboConfigItemToCheck.comboConfigId &&
          areComboProductsEqual(
            comboConfigItemToCheck.comboItemProducts,
            currentComboConfigItem.comboItemProducts
          )
        );
      });
    })
  );
};

export const sortComboItemConfig = (comboConfigs?: ComboConfig[]) => {
  return [...(comboConfigs || [])]
    .sort((a: ComboConfig, b: ComboConfig) => {
      if (
        a.comboTypeSortOrder === undefined &&
        b.comboTypeSortOrder !== undefined
      ) {
        return 1;
      }
      if (
        a.comboTypeSortOrder !== undefined &&
        b.comboTypeSortOrder === undefined
      ) {
        return -1;
      }
      if (
        a.comboTypeSortOrder !== undefined &&
        b.comboTypeSortOrder !== undefined
      ) {
        return a.comboTypeSortOrder - b.comboTypeSortOrder;
      }
      return 0;
    })
    .map((comboConfig: ComboConfig) => {
      return {
        ...comboConfig,
        comboItemProducts: sortComboProducts(comboConfig.comboItemProducts),
      };
    });
};

export const sortComboProducts = (products?: Product[]) => {
  return [...(products || [])]
    .sort((a: Product, b: Product) => {
      if (a.sortOrder === undefined && b.sortOrder !== undefined) {
        return 1;
      }
      if (a.sortOrder !== undefined && b.sortOrder === undefined) {
        return -1;
      }
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      return 0;
    })
    .map((product: Product) => {
      return {
        ...product,
        productOptions: sortProductOptions(product.productOptions),
      };
    });
};

export const findSimilarComboConfig = (
  comboConfigs: ComboConfig[],
  comboConfigToCheck: ComboConfig
) => {
  return comboConfigs.find((currentComboConfig: ComboConfig) => {
    return areComboConfigEqual([currentComboConfig], [comboConfigToCheck]);
  });
};

export const mergeSimilarComboItemConfig = (data: ComboConfig[]) => {
  const mergedData: ComboConfig[] = [];
  data.forEach((item: ComboConfig) => {
    const existingComboConfig = findSimilarComboConfig(mergedData, item);

    if (existingComboConfig) {
      // Create a shallow copy of the existing product
      const updatedProduct = {
        ...existingComboConfig,
        quantity: (existingComboConfig.quantity ?? 0) + (item.quantity ?? 0),
      };
      const existingProductIndex = mergedData.indexOf(existingComboConfig);
      mergedData[existingProductIndex] = updatedProduct;
    } else {
      mergedData.push({ ...item });
    }
  });

  return mergedData;
};

export const filterNonKdsComboItems = (comboConfigs: ComboConfig[]) => {
  return comboConfigs
    .map((comboConfig: ComboConfig) => {
      return {
        ...comboConfig,
        comboItemProducts: comboConfig.comboItemProducts?.filter(
          (product: Product) => product.isForKds
        ),
      };
    })
    .filter(
      (comboConfig: ComboConfig) =>
        comboConfig.comboItemProducts &&
        comboConfig.comboItemProducts.length > 0
    );
};

export const transformComboConfigs = (
  comboConfigs: ComboConfig[] = [],
  response?: Product[]
) => {
  return comboConfigs.map((config: ComboConfig) => ({
    ...config,
    comboItemProducts: config.comboItems
      ?.map((item: ComboItem) => {
        const listProduct = response?.find(
          (p: Product) => p.productId === item.parkProductId
        );
        if (listProduct) {
          return {
            ...listProduct,
            quantity: 1,
            sortOrder: item.sortOrder,
          };
        }
        return {} as Product;
      })
      .filter((product: Product) => Object.keys(product).length > 0),
  }));
};

export const getTotalPriceForComboConfig = (comboConfig: ComboConfig[]) => {
  let comboPrice = 0;
  comboConfig.forEach((comboItem: ComboConfig) => {
    comboItem.comboItemProducts?.forEach((product: Product) => {
      comboPrice +=
        product.productOptions && product.productOptions.length > 0
          ? getTotalPriceForProductOptions(product.productOptions)
          : 0;
    });
  });
  return comboPrice;
};
